// RPG-1 공유 데이터와 순수 계산(소유: 오케스트레이터 → RPG state 레인). 게임·UI·렌더가 함께 읽는다. three import 금지.
// 정본: docs/STUDIO-PLAY-CONTRACT.md. 가격·스탯을 바꾸면 그 문서와 docs/ECONOMY.md를 먼저 고친다.
import { HERO, HIT_STATE, LINK, SKILLS, SKILL_PULL, SKILL_TUNING, type SkillSpec } from './config';
import { SKILL_MAX } from './types';
import { CRESCENT, makeGrowthMods, type GrowthMods } from './growth';
import { BUFF_CAP } from './content/items';
import { TOWN_NPC_SPOTS } from './villageMaps';
import { townBossCleared, townResident } from './townStory';
import type { RebirthStatsIn } from './rebirth';

export type ItemSlot = 'weapon' | 'armor';
/** 마을 시설 NPC 역할. 보석상(마을③)·연금술사(마을④)는 S7(STAGE-WORLD-PRD 5.2)에서 그 마을에만 선다 */
export type NpcRole = 'shop' | 'forge' | 'trainer' | 'jeweler' | 'alchemist' | 'raid';
export type ItemId = 'sword_worn' | 'sword_training' | 'sword_steel' | 'sword_ember' | 'armor_travel' | 'armor_chain' | 'armor_crimson';
/** 장비 외형 단계. 갑옷: 카엘 전신(T1 가죽·T2 강철·T3 진홍 룬, ECONOMY 10절). 무기: 칼날 가장자리 빛(ECONOMY 11절) */
export type GearTier = 1 | 2 | 3;

export interface ItemDef {
  readonly id: ItemId;
  readonly name: string;
  readonly slot: ItemSlot;
  /** 골드. 0 = 처음부터 가진 기본 장비(판매하지 않는다) */
  readonly price: number;
  /** 파는 NPC 역할. null = 판매 안 함 */
  readonly seller: NpcRole | null;
  /** 기본값에 더하는 양 */
  readonly attack: number;
  readonly maxHp: number;
  readonly critChance: number;
  readonly desc: string;
  /** 외형 단계. 갑옷은 카엘 전신, 무기는 칼날 빛. 없으면 T1 */
  readonly tier?: GearTier;
  /**
   * 단조 개선: 이 장비는 upgradeFrom을 가진 사람만 얻을 수 있고(없으면 needs_base), 얻으면 upgradeFrom은 이 장비가 된다
   * (목록·소유에서 빠지고 장착 중이었으면 이 장비로 바뀐다). 저장값이 아니라 장부 계산이다(ECONOMY 11절)
   */
  readonly upgradeFrom?: ItemId;
}

/**
 * 무기 모델은 아직 한 종류뿐이다(단계별 GLB 없음). 단계는 칼날 빛·아이콘·이름으로 구분한다(ECONOMY 11절).
 * 2026-10-20 수치 재설계(LIVE-FUN-PLAN 3.0): 기본 공격 40·체력 360 기준, 장비가 크게 성장한다(공격 +25%/+75%/+200%).
 */
export const ITEMS: readonly ItemDef[] = [
  { id: 'sword_worn', name: '카엘의 낡은 대검', slot: 'weapon', price: 0, seller: null, attack: 0, maxHp: 0, critChance: 0, desc: '처음부터 들고 다닌 대검.', tier: 1 },
  { id: 'sword_training', name: '훈련용 대검', slot: 'weapon', price: 250, seller: 'forge', attack: 10, maxHp: 0, critChance: 0, desc: '균형을 다시 잡아 벼린 대검.', tier: 1 },
  { id: 'sword_steel', name: '강철 대검', slot: 'weapon', price: 700, seller: 'forge', attack: 30, maxHp: 0, critChance: 0.03, desc: '단단한 강철로 날을 세운 대검. 칼날이 청백빛으로 선다.', tier: 2 },
  { id: 'sword_ember', name: '잿불 단조 대검', slot: 'weapon', price: 1200, seller: 'forge', attack: 80, maxHp: 0, critChance: 0.05, desc: '강철 대검을 잿불에 다시 벼린 대검. 칼날에 잿불 금빛이 돈다.', tier: 3, upgradeFrom: 'sword_steel' },
  { id: 'armor_travel', name: '여행자 갑옷', slot: 'armor', price: 350, seller: 'shop', attack: 0, maxHp: 40, critChance: 0, desc: '가볍게 덧댄 가죽 갑옷.', tier: 1 },
  { id: 'armor_chain', name: '사슬 갑옷', slot: 'armor', price: 800, seller: 'shop', attack: 0, maxHp: 120, critChance: 0, desc: '사슬 위에 강철 어깨·정강이받이를 덧댄 갑옷. 입으면 외형이 바뀐다.', tier: 2 },
  { id: 'armor_crimson', name: '진홍 룬 갑옷', slot: 'armor', price: 2400, seller: 'shop', attack: 0, maxHp: 300, critChance: 0, desc: '금테 흑철판에 진홍 룬을 새긴 전설 갑옷. 입으면 외형이 바뀐다.', tier: 3 },
];
export const DEFAULT_WEAPON: ItemId = 'sword_worn';
/** 새 프로필과 v1 이전 프로필에 한 번만 주는 준비금(손상 복구 뒤에는 다시 주지 않는다) */
export const STARTER_GRANT = 300;
/** 골드 상한(조작·오버플로 방지). 이를 넘는 값은 손상으로 본다 */
export const GOLD_MAX = 99_999_999;

const BY_ID = new Map<string, ItemDef>(ITEMS.map((i) => [i.id, i]));
export function itemById(id: string | null | undefined): ItemDef | undefined { return id ? BY_ID.get(id) : undefined; }
/** 장착 갑옷 → 카엘 외형 단계. 없음·모르는 값·무기는 T1 */
export function armorTier(armor: string | null | undefined): GearTier {
  const it = itemById(armor);
  return it && it.slot === 'armor' && it.tier ? it.tier : 1;
}
/** 장착 무기 → 칼날 빛 단계. 없음·모르는 값·갑옷은 T1 */
export function weaponTier(weapon: string | null | undefined): GearTier {
  const it = itemById(weapon);
  return it && it.slot === 'weapon' && it.tier ? it.tier : 1;
}
/** 단조 개선 정의(upgradeFrom이 있는 장비) */
export const UPGRADES: readonly ItemDef[] = ITEMS.filter((i) => i.upgradeFrom);
/** 가진 개선 장비 때문에 그 장비가 된 원본 장비 ID(소유·목록에서 빠진다). 장부 거래로 가진 ID 목록을 받는다 */
export function reforgedBases(ledgerOwned: readonly string[]): string[] {
  const out: string[] = [];
  for (const u of UPGRADES) if (u.upgradeFrom && ledgerOwned.includes(u.id) && !out.includes(u.upgradeFrom)) out.push(u.upgradeFrom);
  return out;
}
/** 원본 장비를 개선한 장비(없으면 undefined) */
export function upgradeOf(base: string | null | undefined): ItemDef | undefined {
  return base ? UPGRADES.find((u) => u.upgradeFrom === base) : undefined;
}

// ── 스킬 설명·주력 스킬 ─────────────────────────────
/** 교관에게서 고르는 주력 스킬: 그 스킬의 재사용 대기시간을 이 배율로 줄인다(마을에서만 바꾼다, 무료) */
export const SKILL_FOCUS_COOLDOWN = 0.8;

export interface SkillInfo { readonly id: 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8; readonly role: string; readonly desc: string }
const pct = (m: number) => Math.round(m * 100) + '%';
/** 연계 설명(LINK 정본에서 만든다): 캔슬 시점과 단계 보너스 */
const cancelText = (i: number) => LINK.cancelAt[i] < 1 ? ' 쓰는 중 ' + pct(LINK.cancelAt[i]) + '가 지나면 다른 스킬로 캔슬된다.' : ' 연계의 마무리 기술로, 캔슬되지 않는다.';
export const SKILL_LINK_RULE = '서로 다른 스킬을 ' + (LINK.window / 60).toFixed(1) + '초 안에 이어 쓰면 연계: 2단 피해 +' + Math.round((LINK.damage[1] - 1) * 100) + '%, 3단 +' + Math.round((LINK.damage[2] - 1) * 100) + '%와 각성 게이지 +' + pct(LINK.finishGauge) + '.';
/** 설명 수치는 config SKILLS에서 바로 만든다(표시와 실제가 어긋나지 않게) */
export const SKILL_INFO: readonly SkillInfo[] = [
  // 돌진: 지나간 길의 균열이 판정 폭(반폭 radius)을 그대로 보여 준다(COMBAT-FEEL 9.3)
  { id: 1, role: '돌진', desc: '앞으로 돌진하며 지나간 길에 균열을 내고 폭 ' + SKILLS[0].radius * 2 + 'm 안의 적을 벤다. 위력 ' + pct(SKILLS[0].mult) + '.' + cancelText(0) },
  { id: 2, role: '광역 연타', desc: '제자리에서 돌며 반경 ' + SKILL_PULL.storm.radius + 'm 안 적을 끌어당기고 반경 ' + SKILLS[1].radius + 'm를 ' + Math.ceil(SKILLS[1].duration / SKILL_TUNING.stormHitInterval) + '번 벤다. 타당 ' + pct(SKILLS[1].mult) + '.' + cancelText(1) },
  { id: 3, role: '내려찍기', desc: '뛰어올라 착지 직전 반경 ' + SKILL_PULL.leap.radius + 'm 안 적을 한데 모아 반경 ' + SKILLS[2].radius + 'm를 내려찍는다. 위력 ' + pct(SKILLS[2].mult) + '.' + cancelText(2) },
  // 진홍 초승달: 성장 트리(진홍 검술 Lv10 층)에서 배운다. 설명은 docs/SKILL-TREE.md 4장과 같다
  { id: 4, role: '초승달', desc: '선딜 뒤 초승달 검기를 전방 ' + CRESCENT.length + 'm로 날린다. 길 위의 적을 폭 ' + CRESCENT.width + 'm로 베고, 벽 너머는 맞히지 않는다. 위력 ' + pct(SKILLS[3].mult) + '.' + cancelText(3) },
  // 천공 올려베기: 영웅 Lv5부터 자동으로 쓸 수 있는 런처(COMBAT-FEEL 2.11)
  { id: 5, role: '런처', desc: '전방 ' + SKILLS[4].radius + 'm를 올려베어 맞은 적을 공중으로 띄운다. 뜬 적에게 기본 공격·스킬로 추가타를 넣고, 착지해 쓰러지면 다운 추격(피해 ' + pct(HIT_STATE.downDamageMult) + '). 위력 ' + pct(SKILLS[4].mult) + '.' + cancelText(4) },
  // C9 새 스킬 6·7·8(Lv15·20·25 자동 습득, docs/SKILL-TREE.md 12절)
  { id: 6, role: '공중 연타', desc: '뛰어올라 공중에서 다섯 번 베어 맞은 적을 다시 띄워 붙잡고, 착지 순간 원형으로 마무리한다. 타당 ' + pct(SKILLS[5].mult) + '.' + cancelText(5) },
  { id: 7, role: '직선 광역', desc: '대검을 내리꽂아 전방으로 균열을 낸다. 일직선 위의 적을 한 번씩 벤다. 위력 ' + pct(SKILLS[6].mult) + '.' + cancelText(6) },
  { id: 8, role: '이동 회전', desc: '붉은 검기를 두르고 앞으로 나아가며 아홉 번 돈다. 쓰는 중 보스 강타에도 끊기지 않는다. 타당 ' + pct(SKILLS[7].mult) + '.' + cancelText(7) },
];

// ── 마을 NPC(게임 판정·렌더 배치·UI 안내의 단일 데이터) ─────────
export type NpcModel = 'npc_shopkeeper' | 'npc_blacksmith' | 'npc_questgiver';
export interface NpcDef {
  readonly id: NpcRole;
  readonly role: NpcRole;
  readonly name: string;
  readonly title: string;
  readonly model: NpcModel;
  /** 전용 모델이 없어 다른 모델을 쓰는 경우 그 사실. QA·문서 전용: 대화·메뉴 등 플레이어 화면에 쓰지 않는다 */
  readonly modelNote: string | null;
  /** 서 있는 자리(월드 m). 시설 문 앞, 걸을 수 있는 바닥 */
  readonly x: number;
  readonly z: number;
  /** 바라보는 방향(rad, 0 = +z) */
  readonly rot: number;
  /** 몸 충돌 반경(m). 영웅이 몸을 통과하지 않는다 */
  readonly radius: number;
  /** 이 거리(m, NPC 중심~영웅 중심) 안에서 상호작용 안내가 뜬다 */
  readonly interactRadius: number;
  readonly greeting: string;
  /** 3D 옷 색 곱(마을 주민, shared/townStory). 없으면 렌더 기본 */
  readonly tint?: number;
  /** UI 초상 CSS 필터(마을 주민). 없으면 ui/rpgArt 역할 기본 */
  readonly artFilter?: string;
  /** 전용 UI 그림 id(마을 주민, shared/townStory). 없으면 역할 기본 그림 */
  readonly art?: string;
}
const E = Math.PI / 2;
/** 순서가 곧 NPC 인덱스(VillageView.nearNpc/menuNpc) */
export const VILLAGE_NPCS: readonly NpcDef[] = [
  { id: 'shop', role: 'shop', name: '미라', title: '잡화상', model: 'npc_shopkeeper', modelNote: null, x: -9.1, z: -8.5, rot: E, radius: 0.4, interactRadius: 2.4,
    greeting: '어서 와요. 던전에 들어가기 전에 몸을 지킬 갑옷부터 챙겨요.' },
  { id: 'forge', role: 'forge', name: '브론', title: '대장장이', model: 'npc_blacksmith', modelNote: null, x: 9.1, z: -8.5, rot: -E, radius: 0.45, interactRadius: 2.4,
    greeting: '칼이 무뎌 보이는군. 골드만 있으면 새로 벼린 대검을 내주지.' },
  { id: 'trainer', role: 'trainer', name: '세르', title: '검술 교관', model: 'npc_questgiver', modelNote: '전용 교관 모델 없음: 의뢰인 모델 재사용', x: -10.1, z: 9.0, rot: E, radius: 0.4, interactRadius: 2.4,
    greeting: '돌진으로 무리 속에 뛰어들고, 폭풍으로 끌어모은 뒤, 낙화로 한 번에 내려찍어라. 서로 다른 기술을 끊김 없이 이어 쓰면 연계가 오른다. 주력으로 삼을 기술도 골라라.' },
];

/**
 * 마을 전용 시설 NPC(S7): 보석상은 마을③ 서리 항구, 연금술사는 마을④ 늪지 장터에만 있다(자리는 shared/villageMaps TOWN_NPC_SPOTS).
 * 전용 모델이 없어 기존 모델을 쓰고 렌더가 옷 색을 바꿔 구분한다(env/npcs.ts tint). 보석상 = 대장간 보석 탭과 같은 일(박기·빼기·합성),
 * 연금술사 = 잡화상 물약·연금 탭과 같은 일(물약 구매·비약 제작). 가짜 버튼 없이 기존 기능으로 바로 이어진다
 */
export const TOWN_ONLY_NPCS: readonly NpcDef[] = [
  { id: 'jeweler', role: 'jeweler', name: '하엘', title: '보석상', model: 'npc_questgiver', modelNote: '전용 보석상 모델 없음: 의뢰인 모델 재사용(푸른 옷 색)',
    x: TOWN_NPC_SPOTS[3].jeweler!.x, z: TOWN_NPC_SPOTS[3].jeweler!.z, rot: TOWN_NPC_SPOTS[3].jeweler!.rot, radius: 0.4, interactRadius: 2.4,
    greeting: '서리 항구에 온 걸 환영해요. 보석은 색이 효과, 등급이 크기예요. 박고 빼고 합치는 건 제게 맡겨요.' },
  { id: 'alchemist', role: 'alchemist', name: '네라', title: '연금술사', model: 'npc_shopkeeper', modelNote: '전용 연금술사 모델 없음: 잡화상 모델 재사용(초록 옷 색)',
    x: TOWN_NPC_SPOTS[4].alchemist!.x, z: TOWN_NPC_SPOTS[4].alchemist!.z, rot: TOWN_NPC_SPOTS[4].alchemist!.rot, radius: 0.4, interactRadius: 2.4,
    greeting: '늪의 약초는 독이자 약이에요. 비약은 제가 달여 드릴게요. 물약도 챙겨 가요.' },
  // 레이드 지휘소(RAID-PRD 1절): 마을②③④. 전용 모델이 없어 대장장이 모델을 쓰고 렌더가 옷 색을 바꾼다(진홍)
  { id: 'raid', role: 'raid', name: '하르칸', title: '레이드 지휘관', model: 'npc_blacksmith', modelNote: '전용 지휘관 모델 없음: 대장장이 모델 재사용(진홍 옷 색)',
    x: TOWN_NPC_SPOTS[2].raid!.x, z: TOWN_NPC_SPOTS[2].raid!.z, rot: TOWN_NPC_SPOTS[2].raid!.rot, radius: 0.45, interactRadius: 2.6,
    greeting: '잿불 비룡이 둥지를 틀었다. 용병 셋을 붙여 주지. 경고 장판을 보면 흩어지고, 날아오르면 줄 밖으로 피해라. 보상은 한 주에 세 번이다.' },
];
/** NPC 인덱스 표(VillageView.nearNpc/menuNpc가 가리킨다): 0..2 = VILLAGE_NPCS(모든 마을), 3 보석상, 4 연금술사 */
export const ALL_NPCS: readonly NpcDef[] = [...VILLAGE_NPCS, ...TOWN_ONLY_NPCS];
/** 인덱스 → NPC 정의(이름·역할·인사말). 자리는 마을마다 다르니 townNpcs로 읽는다 */
export function npcDef(index: number): NpcDef | undefined { return index >= 0 ? ALL_NPCS[index] : undefined; }
/** 마을에 선 NPC(자리 = 그 마을 자리). index = ALL_NPCS 인덱스 */
export interface PlacedNpc extends NpcDef { readonly index: number }
const PLACED = new Map<number, readonly PlacedNpc[]>();
/**
 * 마을 번호(1..) → 그 마을에 선 NPC 목록(같은 배열을 돌려준다, 할당 없음). 마을①은 VILLAGE_NPCS 자리 그대로,
 * 마을②~④는 villageMaps 자리. 자리 표에 없는 역할은 그 마을에 없다(보석상 ③, 연금술사 ④만)
 */
export function townNpcs(town: number): readonly PlacedNpc[] {
  const t = Math.max(1, Math.floor(town) || 1);
  let list = PLACED.get(t);
  if (!list) {
    const spots = TOWN_NPC_SPOTS[t];
    list = ALL_NPCS.flatMap((n, index): PlacedNpc[] => {
      // 마을 주민(TOWN-SCENARIO): 같은 시설이라도 마을마다 다른 사람(이름·직함·옷 색·보스 전 인사). 역할·기능은 그대로
      const who = townResident(t, n.role);
      const me: NpcDef = who ? { ...n, name: who.name, title: who.title, greeting: who.before, tint: who.tint, artFilter: who.artFilter, art: who.art } : n;
      if (!spots) return index < VILLAGE_NPCS.length ? [{ ...me, index }] : [];
      const s = spots[n.role];
      return s ? [{ ...me, index, x: s.x, z: s.z, rot: s.rot }] : [];
    });
    PLACED.set(t, list);
  }
  return list;
}
/** 그 마을에 선 NPC(ALL_NPCS 인덱스). 그 마을에 없는 인덱스면 undefined */
export function townNpcAt(town: number, index: number): PlacedNpc | undefined {
  if (index < 0) return undefined;
  const list = townNpcs(town);
  for (let i = 0; i < list.length; i++) if (list[i].index === index) return list[i];
  return undefined;
}
/** 그 마을 NPC의 지금 인사(지역보스 n-10 전·뒤, TOWN-SCENARIO 3절). cleared = 노말 순차 진행 수 */
export function npcGreeting(town: number, index: number, cleared: number): string {
  const n = townNpcAt(town, index) ?? npcDef(index);
  if (!n) return '';
  const who = townResident(Math.max(1, Math.floor(town) || 1), n.role);
  return who && townBossCleared(town, cleared) ? who.after : n.greeting;
}
/** 그 마을에서 역할 NPC(없으면 undefined) */
export function townNpcByRole(town: number, role: NpcRole): PlacedNpc | undefined {
  return townNpcs(town).find((n) => n.role === role);
}

// ── 파생 능력치 ─────────────────────────────────────
export interface DerivedStats {
  attack: number;
  maxHp: number;
  maxMp: number;
  critChance: number;
  /** 스킬 1..4의 재사용 대기(틱), 인덱스 0..3 */
  readonly skillCooldown: Float32Array;
  /**
   * 스킬별 장비 재사용 감소 하한(틱): 장비 전(기본 × 주력 × 질풍) 재사용 × (1 − BUFF_CAP.cooldown). 장비 옵션 감소와 전설 '칼날의 박자'
   * 치명타 환급을 합쳐도 한 번 쓴 스킬의 재사용 대기가 이보다 짧아지지 않는다(c015, PR #154 리뷰 이월). 질풍·주력은 장비가 아니라 캡 밖이다
   */
  readonly skillCooldownFloor: Float32Array;
  /**
   * 이 능력치를 만든 장착 갑옷의 외형 단계(ECONOMY 14절: max(정의 단계, 등급 단계), 비면 T1). 카엘 몸 T1~T3와 초상은 이것을 따른다
   * (던전 도중 다른 탭 장착은 다음 판에 반영)
   */
  armorTier: GearTier;
  /** 이 능력치를 만든 장착 무기의 외형 단계. 칼날 빛은 이것을 따른다 */
  weaponTier: GearTier;
  // ── 드롭 장비 옵션·세트 효과(PR #154 P1: 보이는 옵션은 모두 실제 계산에 쓴다). 던전 입장 때 사본으로 고정된다 ──
  /** 치명 배율 = HERO.critMult + 치명 피해 옵션 */
  critMult: number;
  /** 스킬 피해·기본 공격 피해·보스 피해·경직(그로기) 피해 배율 */
  skillDmgMult: number;
  basicDmgMult: number;
  bossDmgMult: number;
  staggerMult: number;
  /** 이동 속도 배율 */
  moveSpeedMult: number;
  /** MP 재생 배율 */
  mpRegenMult: number;
  /** 각성 게이지 획득 배율 */
  awakenGainMult: number;
  /** 처치·클리어 EXP 배율, 클리어 골드 배율 */
  xpMult: number;
  goldMult: number;
  /** 흡혈: 준 피해의 이 비율만큼 회복 */
  lifesteal: number;
  /** 방어(고정 방어 × (1 + 방어 %)). 받는 피해 감소는 몹 레벨에 따른 곡선(shared/gear defReduction) */
  def: number;
  /** 받는 피해 감소 옵션·세트(곱으로 쓰는 배율 1 − 합, 방어와 합쳐 BUFF_CAP 50%까지) */
  damageTakenMult: number;
  /** 쓰러질 피해를 HP 1로 버티는 횟수(판마다) */
  lastStand: number;
  /** 전설 고유 효과(던전 입장 때 고정): 저체력 피해·엘리트/보스 피해 가산, 치명타 재사용 대기 감소 틱, 회피 보호막 비율, 처치 회복 비율·MP·각성 */
  lowHpDmg: number; eliteDmg: number; critRefund: number; dodgeShield: number; killHeal: number; killMp: number; killAwaken: number;
  /** 이름 있는 유니크(BALANCE-1028): 초당 MP 고정 회복, 스킬 MP 소모 감소 비율(0..MP_COST_CUT_CAP), 평타 MP 획득 배율 */
  mpRegenFlat: number; mpCostCut: number; basicMpMult: number;
}
export function makeStats(): DerivedStats {
  const s: DerivedStats = { attack: HERO.attack, maxHp: HERO.maxHp, maxMp: HERO.maxMp, critChance: HERO.critChance, skillCooldown: new Float32Array(SKILL_MAX), skillCooldownFloor: new Float32Array(SKILL_MAX), armorTier: 1, weaponTier: 1,
    critMult: HERO.critMult, skillDmgMult: 1, basicDmgMult: 1, bossDmgMult: 1, staggerMult: 1, moveSpeedMult: 1, mpRegenMult: 1, awakenGainMult: 1, xpMult: 1, goldMult: 1,
    lifesteal: 0, def: 0, damageTakenMult: 1, lastStand: 0, lowHpDmg: 0, eliteDmg: 0, critRefund: 0, dodgeShield: 0, killHeal: 0, killMp: 0, killAwaken: 0, mpRegenFlat: 0, mpCostCut: 0, basicMpMult: 1 };
  for (let i = 0; i < SKILLS.length; i++) { s.skillCooldown[i] = SKILLS[i].cooldown; s.skillCooldownFloor[i] = Math.round(SKILLS[i].cooldown * (1 - BUFF_CAP.cooldown)); }
  return s;
}
/** 성장 보정이 없을 때의 기본값(마을 장비 창 미리보기 등) */
const NEUTRAL_MODS: GrowthMods = makeGrowthMods();
/**
 * 기본값 + 장착 장비(6부위, gear.v1) + 성장(레벨·스킬 트리, growth.ts growthMods 결과) + 주력 스킬.
 * 장착·로드·던전 입장·레벨업·배우기 때만 부른다(프레임마다 부르지 않는다).
 * 옛 상점 무기·갑옷(ITEMS)은 2026-10-26 장비 통합(ECONOMY 14절)부터 능력치·외형에 쓰지 않는다 — 같은 능력치의 옛 장비 개체로 전환돼 gear에 들어 있다.
 * 최대 HP: (기본 + 장비 + 레벨·트리 가산) × 트리 배율, 정수로 반올림. 재사용: 기본 × 주력 × 질풍.
 */
/**
 * rebirth(레인 R, REBIRTH-MODES-SPEC 2.2): 환생 포인트 배분 — 공격 ×(1+atkPct), 최대 HP ×(1+hpPct), 치명 +crit. 없으면 그대로(기존 호출 호환).
 * 레벨 동기화는 장비 부위마다 아이템 레벨이 필요해 장비 합계(shared/gear sumGear syncLevel)에서 이미 곱해져 들어온다
 */
export function deriveStats(out: DerivedStats, skillFocus: number, mods: GrowthMods = NEUTRAL_MODS, gear: GearStatsIn = NO_GEAR, skills: readonly SkillSpec[] = SKILLS, rebirth?: RebirthStatsIn): DerivedStats {
  out.attack = HERO.attack; out.maxHp = HERO.maxHp; out.maxMp = HERO.maxMp; out.critChance = HERO.critChance;
  out.armorTier = lookTier(gear.armorTier);
  out.weaponTier = lookTier(gear.weaponTier);
  out.attack += mods.attackAdd;
  // 드롭 장비 6부위(D2a, shared/gear.ts sumGear): 고정 공격·HP를 더하고 % 옵션을 곱한다. 장비가 없으면 0·×1이라 기존 값과 같다
  out.attack = Math.round((out.attack + gear.attack) * (1 + gear.atkPct));
  out.maxHp = Math.round((HERO.maxHp + mods.maxHpAdd + gear.maxHp) * mods.maxHpMult * (1 + gear.hpPct));
  out.maxMp = HERO.maxMp + mods.maxMpAdd + (gear.maxMpFlat ?? 0);
  out.critChance += mods.critAdd + gear.crit;
  if (rebirth) {
    out.attack = Math.round(out.attack * (1 + rebirth.atkPct));
    out.maxHp = Math.round(out.maxHp * (1 + rebirth.hpPct));
    out.critChance += rebirth.crit;
  }
  // c022: 스킬 표는 영웅별(skills, 기본 카엘 SKILLS). 표 밖 칸은 0
  for (let i = skills.length; i < out.skillCooldown.length; i++) { out.skillCooldown[i] = 0; out.skillCooldownFloor[i] = 0; }
  for (let i = 0; i < skills.length; i++) {
    const focus = skillFocus === i + 1 ? SKILL_FOCUS_COOLDOWN : 1;
    const pre = skills[i].cooldown * focus * mods.cooldownMult;
    // 장비 쿨감은 sumGear가 BUFF_CAP 안으로 자르지만, 미리보기 호출(직접 넣은 합계)도 하한 아래로 내려가지 않게 한 번 더 자른다
    out.skillCooldownFloor[i] = Math.round(pre * (1 - BUFF_CAP.cooldown));
    out.skillCooldown[i] = Math.max(out.skillCooldownFloor[i], Math.round(pre * (1 - gear.cooldown)));
  }
  out.critMult = HERO.critMult + (gear.critDmg ?? 0);
  out.skillDmgMult = 1 + (gear.skillDmg ?? 0);
  out.basicDmgMult = 1 + (gear.basicDmg ?? 0);
  out.bossDmgMult = 1 + (gear.bossDmg ?? 0);
  out.staggerMult = 1 + (gear.staggerDmg ?? 0);
  out.moveSpeedMult = 1 + (gear.moveSpd ?? 0);
  out.mpRegenMult = 1 + (gear.mpRegen ?? 0);
  out.awakenGainMult = 1 + (gear.awakenGauge ?? 0);
  out.xpMult = 1 + (gear.xp ?? 0);
  out.goldMult = 1 + (gear.gold ?? 0);
  out.lifesteal = gear.lifesteal ?? 0;
  out.def = Math.round((gear.def ?? 0) * (1 + (gear.defPct ?? 0)));
  out.damageTakenMult = 1 - (gear.damageTaken ?? 0);
  out.lastStand = gear.lastStand ?? 0;
  out.lowHpDmg = gear.lowHpDmg ?? 0; out.eliteDmg = gear.eliteDmg ?? 0; out.critRefund = gear.critRefund ?? 0; out.dodgeShield = gear.dodgeShield ?? 0;
  out.killHeal = gear.killHeal ?? 0; out.killMp = gear.killMp ?? 0; out.killAwaken = gear.killAwaken ?? 0;
  out.mpRegenFlat = gear.mpFlat ?? 0; out.mpCostCut = gear.mpCostCut ?? 0; out.basicMpMult = 1 + (gear.basicMp ?? 0);
  return out;
}
/**
 * deriveStats가 읽는 장비 합계 칸(shared/gear.ts GearTotals). 앞 6칸은 필수, 나머지는 없으면 0(미리보기 호출 호환).
 * 공격 속도는 동작 프레임 데이터를 바꿔야 해 아직 효과가 없어 드롭 옵션 풀에서 뺐다(content/items AFFIXES atkSpd slots 비움).
 */
export interface GearStatsIn {
  readonly attack: number; readonly maxHp: number; readonly crit: number; readonly atkPct: number; readonly hpPct: number; readonly cooldown: number;
  readonly critDmg?: number; readonly skillDmg?: number; readonly basicDmg?: number; readonly bossDmg?: number; readonly staggerDmg?: number;
  readonly moveSpd?: number; readonly mpRegen?: number; readonly awakenGauge?: number; readonly xp?: number; readonly gold?: number;
  readonly lifesteal?: number; readonly def?: number; readonly defPct?: number; readonly damageTaken?: number; readonly lastStand?: number;
  readonly lowHpDmg?: number; readonly eliteDmg?: number; readonly critRefund?: number; readonly dodgeShield?: number; readonly killHeal?: number; readonly killMp?: number; readonly killAwaken?: number;
  readonly mpFlat?: number; readonly mpCostCut?: number; readonly basicMp?: number; readonly maxMpFlat?: number;
  /** 장착 갑옷·무기 외형 단계(shared/gear sumGear, 0·없음 = T1) */
  readonly armorTier?: number; readonly weaponTier?: number;
}
function lookTier(t: number | undefined): GearTier { return t === 2 || t === 3 ? t : 1; }
const NO_GEAR: GearStatsIn = { attack: 0, maxHp: 0, crit: 0, atkPct: 0, hpPct: 0, cooldown: 0 };
